import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, Video, Image as ImageIcon, Type, ChevronUp, ChevronDown, Trash2, X, Eye, Edit3, Save,
  Youtube, HelpCircle, ArrowLeft, MousePointerClick, Tag, Star, Clock, Mail, Images, LayoutGrid,
  Lightbulb, Minus, MoveVertical, CreditCard, Maximize2, Crown, User, Settings, LayoutDashboard,
  LayoutTemplate, Sparkles, Pencil, PhoneCall, Facebook, Instagram, MessageCircle, Wand2, Upload,
  Loader2, ImagePlus, Palette,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { Helmet } from 'react-helmet-async';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useSalePage } from '../contexts/SalePageContext';
import { useAuthContext } from '../contexts/AuthContext';
import { getUploadUrlWithQuota } from '../services/uploadWithQuota';
import ColorSwatchPicker from './SalePageBlocks/ColorSwatchPicker';
import VideoBlock from './SalePageBlocks/VideoBlock';
import ImageBlock from './SalePageBlocks/ImageBlock';
import RichTextBlock from './SalePageBlocks/RichTextBlock';
import YouTubeBlock from './SalePageBlocks/YouTubeBlock';
import CTAButtonBlock from './SalePageBlocks/CTAButtonBlock';
import PriceBlock from './SalePageBlocks/PriceBlock';
import ReviewBlock from './SalePageBlocks/ReviewBlock';
import FAQBlock from './SalePageBlocks/FAQBlock';
import CountdownBlock from './SalePageBlocks/CountdownBlock';
import ContactFormBlock from './SalePageBlocks/ContactFormBlock';
import GalleryBlock from './SalePageBlocks/GalleryBlock';
import ComparisonTableBlock from './SalePageBlocks/ComparisonTableBlock';
import IconTextBlock from './SalePageBlocks/IconTextBlock';
import DividerBlock from './SalePageBlocks/DividerBlock';
import SpacerBlock from './SalePageBlocks/SpacerBlock';
import PaymentBlock from './SalePageBlocks/PaymentBlock';
import RealSizeImageBlock from './SalePageBlocks/RealSizeImageBlock';

interface ContentBlock {
  id: string;
  type: 'video' | 'image' | 'text' | 'youtube' | 'social' | 'cta' | 'price' | 'review' | 'faq' | 'countdown' | 'contact' | 'gallery' | 'comparison' | 'icontext' | 'divider' | 'spacer' | 'payment' | 'real-size-image';
  data: any;
}

interface SocialLinks {
  facebook?: string;
  instagram?: string;
  line?: string;
  phone?: string;
}

const FONT = '"IBM Plex Sans Thai", system-ui, sans-serif';
const HEAD = "'Anuphan', sans-serif";
const INK = '#0E3A40';
const SWATCHES = ['#FFFFFF', '#F3F8F8', '#0E3A40', '#00B4A1', '#3A93B9'];
const RECENT_COLORS_KEY = 'saledi_recent_bg_colors';
const RECENT_COLORS_MAX = 5;
const SWATCH_NAMES: Record<string, string> = {
  '#FFFFFF': 'ขาว',
  '#F3F8F8': 'ขาวมินต์',
  '#0E3A40': 'เขียวเข้ม',
  '#00B4A1': 'เขียวมิ้นต์',
  '#3A93B9': 'ฟ้า',
  '#5B6FCB': 'ม่วง',
};

type BlockDef = { type: ContentBlock['type']; label: string; icon: React.ComponentType<any>; bg: string; color: string };

const BLOCK_GROUPS: { name: string; items: BlockDef[] }[] = [
  {
    name: 'เนื้อหาหลัก',
    items: [
      { type: 'text', label: 'ข้อความ', icon: Type, bg: 'rgba(58,147,185,0.1)', color: '#276E8A' },
      { type: 'image', label: 'รูปภาพ', icon: ImageIcon, bg: 'rgba(0,180,161,0.1)', color: '#0E7C86' },
      { type: 'video', label: 'วิดีโอ', icon: Video, bg: 'rgba(91,111,203,0.1)', color: '#4A57A8' },
      { type: 'youtube', label: 'YouTube', icon: Youtube, bg: 'rgba(220,38,38,0.1)', color: '#C0392B' },
      { type: 'gallery', label: 'แกลเลอรี่', icon: Images, bg: 'rgba(91,111,203,0.1)', color: '#4A57A8' },
      { type: 'icontext', label: 'ไอคอน + ข้อความ', icon: Lightbulb, bg: 'rgba(58,147,185,0.1)', color: '#276E8A' },
      { type: 'real-size-image', label: 'รูปภาพขนาดจริง', icon: Maximize2, bg: 'rgba(0,180,161,0.1)', color: '#0E7C86' },
    ],
  },
  {
    name: 'ช่วยปิดการขาย',
    items: [
      { type: 'cta', label: 'ปุ่ม CTA', icon: MousePointerClick, bg: 'rgba(255,122,69,0.1)', color: '#B3552A' },
      { type: 'price', label: 'ราคาสินค้า', icon: Tag, bg: 'rgba(0,180,161,0.1)', color: '#0E7C86' },
      { type: 'review', label: 'รีวิวลูกค้า', icon: Star, bg: 'rgba(58,147,185,0.1)', color: '#276E8A' },
      { type: 'countdown', label: 'นับถอยหลัง', icon: Clock, bg: 'rgba(0,180,161,0.1)', color: '#0E7C86' },
      { type: 'comparison', label: 'ตารางเปรียบเทียบ', icon: LayoutGrid, bg: 'rgba(0,180,161,0.1)', color: '#0E7C86' },
      { type: 'payment', label: 'ระบบชำระเงิน', icon: CreditCard, bg: 'rgba(255,122,69,0.1)', color: '#B3552A' },
    ],
  },
  {
    name: 'อื่นๆ',
    items: [
      { type: 'faq', label: 'FAQ', icon: HelpCircle, bg: 'rgba(91,111,203,0.1)', color: '#4A57A8' },
      { type: 'contact', label: 'ฟอร์มติดต่อ', icon: Mail, bg: 'rgba(58,147,185,0.1)', color: '#276E8A' },
      { type: 'divider', label: 'เส้นแบ่ง', icon: Minus, bg: 'rgba(14,58,64,0.06)', color: '#5B7D80' },
      { type: 'spacer', label: 'ช่องว่าง', icon: MoveVertical, bg: 'rgba(14,58,64,0.06)', color: '#5B7D80' },
    ],
  },
];

const planLabel = (p?: string) => (p === 'basic' ? 'Basic' : p === 'premium' ? 'Premium' : p === 'premium_vip' ? 'Premium VIP' : 'ทดสอบระบบ');

const SalePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscription, loading: subscriptionLoading, isSubscriptionExpired } = useSalePage();
  const { user } = useAuthContext();
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [recentColors, setRecentColors] = useState<string[]>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(RECENT_COLORS_KEY) || '[]');
      if (Array.isArray(saved) && saved.length) return saved.slice(0, RECENT_COLORS_MAX);
    } catch { /* ignore */ }
    return SWATCHES.slice(0, RECENT_COLORS_MAX);
  });
  const [socialLinks, setSocialLinks] = useState<SocialLinks>({});
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [salePageId, setSalePageId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [narrow, setNarrow] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactEditing, setContactEditing] = useState(false);
  const [tempSocialLinks, setTempSocialLinks] = useState<SocialLinks>({});
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiDescription, setAiDescription] = useState('');
  const [aiImages, setAiImages] = useState<File[]>([]);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const [aiStageIdx, setAiStageIdx] = useState(0);
  const [aiUseCustomColors, setAiUseCustomColors] = useState(false);
  const [aiBrandPrimary, setAiBrandPrimary] = useState('#00B4A1');
  const [aiBrandSecondary, setAiBrandSecondary] = useState('#FF7A45');
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [themeColor, setThemeColor] = useState('#00B4A1');
  const menuRef = useRef<HTMLDivElement>(null);
  const customPickerRef = useRef<HTMLDivElement>(null);
  const themePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 760);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
      if (customPickerRef.current && !customPickerRef.current.contains(e.target as Node)) {
        setCustomPickerOpen((open) => {
          if (open) rememberRecentColor(backgroundColor);
          return false;
        });
      }
      if (themePickerRef.current && !themePickerRef.current.contains(e.target as Node)) {
        setThemePickerOpen(false);
      }
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [backgroundColor]);

  const AI_STAGES = ['กำลังอ่านรายละเอียดสินค้า...', 'กำลังวางโครงสร้าง 8 ส่วน...', 'กำลังเขียนคำโฆษณา...', 'กำลังจัดวางรูปภาพ...', 'เก็บรายละเอียดสุดท้าย...'];
  useEffect(() => {
    if (!aiGenerating) { setAiStageIdx(0); return; }
    const t = setInterval(() => setAiStageIdx((i) => Math.min(i + 1, AI_STAGES.length - 1)), 4000);
    return () => clearInterval(t);
  }, [aiGenerating]);

  // Only inserts a color that isn't already in the list — existing swatches keep their position,
  // only a genuinely new custom pick gets added to the front. Also only call once a color choice
  // is FINAL (color-picker closed), since the native color <input> fires onChange continuously
  // while dragging.
  const rememberRecentColor = (color: string) => {
    setRecentColors((prev) => {
      if (prev.some((c) => c.toLowerCase() === color.toLowerCase())) return prev;
      const next = [color, ...prev].slice(0, RECENT_COLORS_MAX);
      try { localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  // Swatch click: select only, never reorders the row.
  const pickBgColor = (color: string) => {
    setBackgroundColor(color);
  };

  // เปลี่ยนสีปุ่ม/สีเน้นของทุกบล็อกในหน้าเดียวด้วยคลิกเดียว
  const applyButtonColorToAll = (hex: string) => {
    setBlocks((prev) => prev.map((b) => {
      if (b.type === 'cta') return { ...b, data: { ...b.data, buttonColor: hex } };
      if (b.type === 'contact') return { ...b, data: { ...b.data, buttonColor: hex } };
      if (b.type === 'payment') return { ...b, data: { ...b.data, accentColor: hex } };
      if (b.type === 'comparison') {
        const columns = (b.data?.columns || []).map((c: any) => ({ ...c, highlightColor: hex }));
        return { ...b, data: { ...b.data, columns } };
      }
      return b;
    }));
  };

  // Auto-generate slug from title
  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50);
  };

  // Generate preview URL based on subscription
  const getPreviewUrl = () => {
    if (subscription?.username && (subscription?.hasPathUrl || subscription?.hasSubdomain)) {
      // Subdomain form is not available on saledi.web.app — always use path form
      return `saledi.web.app/${subscription.username}`;
    } else {
      // Free URL using userId
      return `saledi.web.app/s/${user?.uid || ''}`;
    }
  };

  // Get feature limits based on plan
  const getFeatureLimits = () => {
    const plan = subscription?.plan || 'free';

    // Basic Plan limits
    if (plan === 'basic' || plan === 'start') {
      return {
        video: 3,
        image: Infinity,
        youtube: Infinity,
        text: Infinity,
        cta: Infinity,
        price: Infinity,
        payment: 0,
        review: 3,
        faq: 5,
        social: Infinity,
        gallery: 0,
        icontext: 0,
        countdown: 0,
        comparison: 0,
        contact: 0,
        divider: Infinity,
        spacer: Infinity,
        'real-size-image': 0
      };
    }

    // Premium Plan limits (Sale Page subscription only) — Premium VIP inherits everything Premium has, plus AI page generation
    if (plan === 'premium' || plan === 'premium_vip') {
      return {
        video: 6,
        image: Infinity,
        youtube: Infinity,
        text: Infinity,
        cta: Infinity,
        price: Infinity,
        payment: Infinity,
        review: Infinity,
        faq: Infinity,
        social: Infinity,
        gallery: Infinity,
        icontext: Infinity,
        countdown: Infinity,
        comparison: Infinity,
        contact: Infinity,
        divider: Infinity,
        spacer: Infinity,
        'real-size-image': Infinity
      };
    }

    // Free/Trial limits (default) — freemium: no self-uploaded clips (YouTube is free), 5 images.
    // Premium-only blocks are 0 here (consistent with Basic) so they can't be used without paying.
    return {
      video: 0,
      image: 5,
      youtube: Infinity,
      text: Infinity,
      cta: Infinity,
      price: Infinity,
      payment: 0,
      review: 1,
      faq: 3,
      social: Infinity,
      gallery: 0,
      icontext: 0,
      countdown: 0,
      comparison: 0,
      contact: 0,
      divider: Infinity,
      spacer: Infinity,
      'real-size-image': 0
    };
  };

  // Check if user can add a specific block type
  const canAddBlock = (type: ContentBlock['type']): { allowed: boolean; message?: string } => {
    const limits = getFeatureLimits();
    const plan = subscription?.plan || 'free';

    // Count current blocks of this type
    const currentCount = blocks.filter(b => b.type === type).length;
    const limit = limits[type as keyof typeof limits] || 0;

    // Check if limit is reached
    if (limit === 0) {
      return {
        allowed: false,
        message: `ฟีเจอร์นี้ใช้ได้เฉพาะแพ็คเก็จ Premium เท่านั้น`
      };
    }

    if (currentCount >= limit && limit !== Infinity) {
      const blockName = {
        video: 'วิดีโอ',
        image: 'รูปภาพ',
        text: 'ข้อความ',
        youtube: 'YouTube',
        social: 'โซเชียล',
        cta: 'ปุ่ม CTA',
        price: 'ราคาสินค้า',
        review: 'รีวิว',
        faq: 'FAQ',
        countdown: 'นับถอยหลัง',
        contact: 'ฟอร์มติดต่อ',
        gallery: 'แกลเลอรี่',
        comparison: 'ตารางเปรียบเทียบ',
        icontext: 'ไอคอน + ข้อความ',
        divider: 'เส้นแบ่ง',
        spacer: 'ช่องว่าง',
        payment: 'ระบบชำระเงิน',
        'real-size-image': 'รูปภาพขนาดจริง'
      }[type] || type;

      return {
        allowed: false,
        message: `แพ็คเก็จของคุณสามารถเพิ่ม${blockName}ได้สูงสุด ${limit} รายการ`
      };
    }

    return { allowed: true };
  };

  const addBlock = (type: ContentBlock['type']) => {
    // Check if user can add this block type
    const { allowed, message } = canAddBlock(type);
    if (!allowed) {
      alert(message || 'ไม่สามารถเพิ่มบล็อกนี้ได้');
      return;
    }

    let initialData: any = null;

    switch(type) {
      case 'text':
        initialData = { html: '', align: 'left' };
        break;
      case 'cta':
        initialData = { text: 'คลิกที่นี่', url: '', buttonColor: '#f97316', textColor: '#ffffff', size: 'medium' };
        break;
      case 'price':
        initialData = { originalPrice: '', salePrice: '', currency: '฿', discount: '', promotionText: '' };
        break;
      case 'review':
        initialData = { reviews: [] };
        break;
      case 'faq':
        initialData = { faqs: [] };
        break;
      case 'countdown':
        initialData = { endDate: '', title: 'โปรโมชั่นพิเศษ!', textColor: '#ffffff', backgroundColor: '#f97316' };
        break;
      case 'contact':
        initialData = { title: 'ติดต่อเรา', subtitle: '', buttonText: 'ส่งข้อความ', buttonColor: '#f97316', fields: { name: true, email: true, phone: true, message: true } };
        break;
      case 'gallery':
        initialData = { images: [], layout: 'grid' };
        break;
      case 'comparison':
        initialData = { columns: [] };
        break;
      case 'icontext':
        initialData = { items: [], columns: 3 };
        break;
      case 'divider':
        initialData = { style: 'solid', color: '#e5e7eb', thickness: 2, spacing: 20 };
        break;
      case 'spacer':
        initialData = { height: 50 };
        break;
      case 'payment':
        initialData = {
          qrCodeUrl: '',
          accountName: '',
          bankName: '',
          accountNumber: '',
          packages: [],
          successMessage: 'ขอบคุณที่สั่งซื้อ! เราจะติดต่อกลับเร็วๆ นี้',
          lineUrl: '',
          phoneNumber: '',
          email: ''
        };
        break;
      case 'real-size-image':
        initialData = { imageUrl: '', alt: '', width: '100%', height: 'auto' };
        break;
    }

    const newBlock: ContentBlock = {
      id: Date.now().toString(),
      type,
      data: initialData
    };

    setBlocks([...blocks, newBlock]);
    setPickerOpen(false);
  };

  const updateBlock = (id: string, data: any) => {
    setBlocks(blocks.map(block => block.id === id ? { ...block, data } : block));
  };

  const deleteBlock = (id: string) => {
    setBlocks(blocks.filter(block => block.id !== id));
  };

  const moveBlock = (id: string, direction: 'up' | 'down') => {
    const index = blocks.findIndex(block => block.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === blocks.length - 1) return;

    const newBlocks = [...blocks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];
    setBlocks(newBlocks);
  };

  useEffect(() => {
    const loadSalePage = async () => {
      const id = searchParams.get('salePageId');
      if (id && user) {
        setIsLoadingPage(true);
        try {
          const salePageDoc = await getDoc(doc(db, 'salepages', id));
          if (salePageDoc.exists()) {
            const data = salePageDoc.data();
            if (data.userId === user.uid) {
              setSalePageId(id);
              setTitle(data.title || '');
              setSlug(data.slug || '');
              setBlocks(data.blocks || []);
              setBackgroundColor(data.backgroundColor || '#ffffff');
              setSocialLinks(data.socialLinks || {});
            }
          }
        } catch (error) {
          console.error('Error loading sale page:', error);
        } finally {
          setIsLoadingPage(false);
        }
      }
    };
    loadSalePage();
  }, [searchParams, user]);

  const handleSave = async () => {
    if (!title.trim()) {
      alert('กรุณาระบุชื่อ Sale Page');
      return;
    }

    // Auto-generate slug if empty
    const finalSlug = slug.trim() || generateSlug(title) || `page-${Date.now()}`;
    setSlug(finalSlug);

    setIsSaving(true);
    try {
      const saveSalePageFn = httpsCallable(functions, 'saveSalePage');
      const result = await saveSalePageFn({
        salePageId,
        title: title.trim(),
        slug: finalSlug,
        blocks,
        backgroundColor,
        socialLinks
      }) as { data: { success: boolean; salePageId: string; url: string } };

      if (result.data.success) {
        setSalePageId(result.data.salePageId);
        setGeneratedUrl(result.data.url);
        setShowUrlModal(true);
      }
    } catch (error: any) {
      console.error('Error saving sale page:', error);
      alert(error.message || 'เกิดข้อผิดพลาดในการบันทึก Sale Page');
    } finally {
      setIsSaving(false);
    }
  };

  const onSaveClick = () => {
    if (cannotSave) {
      alert('แพ็คเก็จหมดอายุแล้ว กรุณาต่ออายุเพื่อใช้งานฟีเจอร์นี้');
      return;
    }
    handleSave();
  };

  const saveContact = () => {
    setSocialLinks(tempSocialLinks);
    setContactEditing(false);
  };
  const openContactEdit = () => {
    setTempSocialLinks(socialLinks);
    setContactEditing(true);
  };
  const hasAnyLink = Object.values(socialLinks).some(l => l && l.trim() !== '');

  const isVip = subscription?.plan === 'premium_vip';

  const openAiModal = () => {
    if (!isVip) {
      alert('ฟีเจอร์สร้าง Sale Page ด้วย AI ใช้ได้เฉพาะแพ็คเกจ Premium VIP เท่านั้น');
      navigate('/salepage/renew');
      return;
    }
    setAiError('');
    setAiModalOpen(true);
  };

  const addAiImages = (files: FileList | null) => {
    if (!files) return;
    const next = [...aiImages, ...Array.from(files).filter(f => f.type.startsWith('image/'))].slice(0, 4);
    setAiImages(next);
  };
  const removeAiImage = (index: number) => setAiImages(aiImages.filter((_, i) => i !== index));

  const generateWithAi = async () => {
    if (!aiDescription.trim()) {
      setAiError('กรุณาใส่คำอธิบายสินค้า/บริการก่อน');
      return;
    }
    if (blocks.length > 0 && !window.confirm('การสร้างด้วย AI จะแทนที่เนื้อหาปัจจุบันทั้งหมด ต้องการดำเนินการต่อไหม?')) {
      return;
    }
    setAiGenerating(true);
    setAiError('');
    try {
      const imageUrls: string[] = [];
      for (const file of aiImages) {
        const result = await getUploadUrlWithQuota(file.name, file.type, file.size, file);
        imageUrls.push(result.publicUrl);
      }

      const fn = httpsCallable(functions, 'generateSalePageAI');
      const res: any = await fn({
        description: aiDescription.trim(),
        imageUrls,
        ...(aiUseCustomColors ? { brandPrimary: aiBrandPrimary, brandSecondary: aiBrandSecondary } : {}),
      });
      const generatedBlocks: ContentBlock[] = res?.data?.blocks || [];
      if (!generatedBlocks.length) throw new Error('AI ไม่สามารถสร้างเนื้อหาได้ กรุณาลองใหม่');

      setBlocks(generatedBlocks);
      setAiModalOpen(false);
      setAiDescription('');
      setAiImages([]);
    } catch (e: any) {
      console.error('[SalePage] AI generate error:', e);
      setAiError(e?.message || 'เกิดข้อผิดพลาดในการสร้างด้วย AI กรุณาลองใหม่');
    } finally {
      setAiGenerating(false);
    }
  };

  const isExpired = isSubscriptionExpired();
  // Freemium: users with no package yet may build & save a DRAFT. Only users whose paid
  // package has actually expired are blocked from saving (must renew).
  const cannotSave = !!subscription && isExpired;

  const overlayStyle: React.CSSProperties = narrow
    ? { position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(14,58,64,0.45)', display: 'flex', alignItems: 'flex-end' }
    : { position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(14,58,64,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 };
  const sheetStyle: React.CSSProperties = narrow
    ? { width: '100%', maxHeight: '78vh', background: '#fff', borderRadius: '22px 22px 0 0', padding: '16px 16px calc(env(safe-area-inset-bottom,0px) + 16px)', display: 'flex', flexDirection: 'column', gap: 14, overflow: 'hidden' }
    : { width: '100%', maxWidth: 640, maxHeight: '80vh', background: '#fff', borderRadius: 24, padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 14, overflow: 'hidden', boxShadow: '0 40px 80px -30px rgba(14,58,64,0.4)' };
  const groupGridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: `repeat(${narrow ? 3 : 5},1fr)`, gap: 8 };

  const ctrlBtn = (bg: string, color: string, disabled?: boolean): React.CSSProperties => ({
    width: 30, height: 30, borderRadius: 10, border: 0, background: bg, color,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1, flex: '0 0 auto',
  });

  const renderBlock = (block: ContentBlock) => {
    const commonProps = { data: block.data, onChange: (data: any) => updateBlock(block.id, data) };
    switch (block.type) {
      case 'video': return <VideoBlock {...commonProps} />;
      case 'image': return <ImageBlock {...commonProps} />;
      case 'text': return <RichTextBlock {...commonProps} />;
      case 'youtube': return <YouTubeBlock {...commonProps} />;
      case 'cta': return <CTAButtonBlock {...commonProps} onDelete={() => deleteBlock(block.id)} isPreview={isPreviewMode} />;
      case 'price': return <PriceBlock {...commonProps} onDelete={() => deleteBlock(block.id)} isPreview={isPreviewMode} />;
      case 'review': return <ReviewBlock {...commonProps} onDelete={() => deleteBlock(block.id)} isPreview={isPreviewMode} />;
      case 'faq': return <FAQBlock {...commonProps} onDelete={() => deleteBlock(block.id)} isPreview={isPreviewMode} />;
      case 'countdown': return <CountdownBlock {...commonProps} onDelete={() => deleteBlock(block.id)} isPreview={isPreviewMode} />;
      case 'contact': return <ContactFormBlock {...commonProps} onDelete={() => deleteBlock(block.id)} isPreview={isPreviewMode} />;
      case 'gallery': return <GalleryBlock {...commonProps} onDelete={() => deleteBlock(block.id)} isPreview={isPreviewMode} />;
      case 'comparison': return <ComparisonTableBlock {...commonProps} onDelete={() => deleteBlock(block.id)} isPreview={isPreviewMode} />;
      case 'icontext': return <IconTextBlock {...commonProps} onDelete={() => deleteBlock(block.id)} isPreview={isPreviewMode} />;
      case 'divider': return <DividerBlock {...commonProps} onDelete={() => deleteBlock(block.id)} isPreview={isPreviewMode} />;
      case 'spacer': return <SpacerBlock {...commonProps} onDelete={() => deleteBlock(block.id)} isPreview={isPreviewMode} />;
      case 'payment': return <PaymentBlock {...commonProps} onDelete={() => deleteBlock(block.id)} isPreview={isPreviewMode} />;
      case 'real-size-image': return <RealSizeImageBlock {...commonProps} onDelete={() => deleteBlock(block.id)} isPreview={isPreviewMode} />;
      default: return null;
    }
  };

  const PickerModal = (
    <div onClick={() => setPickerOpen(false)} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={sheetStyle}>
        {narrow && <div style={{ width: 36, height: 4, borderRadius: 999, background: 'rgba(14,58,64,0.15)', margin: '0 auto' }} />}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 16 }}>เลือกบล็อกที่ต้องการเพิ่ม</span>
          <button type="button" onClick={() => setPickerOpen(false)} style={{ width: 30, height: 30, borderRadius: 999, border: 0, background: '#F3F8F8', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X style={{ width: 15, height: 15, color: '#5B7D80' }} />
          </button>
        </div>
        <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18, paddingBottom: 4 }}>
          {BLOCK_GROUPS.map((grp) => (
            <div key={grp.name}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: '#5B7D80', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>{grp.name}</div>
              <div style={groupGridStyle}>
                {grp.items.map((bt) => {
                  const Icon = bt.icon;
                  return (
                    <button key={bt.type} type="button" onClick={() => addBlock(bt.type)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, background: '#F3F8F8', border: '1px solid rgba(14,58,64,0.06)', borderRadius: 14, padding: '12px 6px', cursor: 'pointer' }}>
                      <span style={{ width: 32, height: 32, borderRadius: 10, background: bt.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon style={{ width: 15, height: 15, color: bt.color }} />
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 500, color: INK, textAlign: 'center', lineHeight: 1.25 }}>{bt.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (subscriptionLoading || isLoadingPage) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F3F8F8' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid #00B4A1', borderTopColor: 'transparent', borderRadius: '999px', margin: '0 auto 12px' }} className="animate-spin" />
          <p style={{ color: '#5B7D80', fontFamily: FONT }}>กำลังโหลด Sale Page Builder...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#F3F8F8', color: INK, fontFamily: FONT }}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Anuphan:wght@400;500;600;700&family=IBM+Plex+Sans+Thai:wght@400;500;600&display=swap" rel="stylesheet" />
      </Helmet>
      <style>{`
        .sp-toolbar-input::placeholder{color:rgba(255,255,255,0.45)}
        .sp-color-picker.react-colorful{width:100%;height:140px;border-radius:14px;overflow:hidden}
        .sp-color-picker .react-colorful__saturation{border-radius:14px 14px 0 0}
        .sp-color-picker .react-colorful__hue{height:16px;border-radius:0 0 14px 14px}
        .sp-color-picker .react-colorful__pointer{width:16px;height:16px;border-width:2px}
        .sp-hex-input{border:0;outline:none;padding:9px 4px;font-size:13px;font-family:${FONT};width:100%;text-transform:uppercase;background:transparent}
      `}</style>

      {!isPreviewMode && (
        <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(243,248,248,0.9)', backdropFilter: 'blur(18px)', borderBottom: '1px solid rgba(14,58,64,0.08)' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto', padding: '12px clamp(16px,3vw,32px)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
              <span style={{ width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg, #00B4A1, #3A93B9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: HEAD, fontWeight: 700, fontSize: 16, boxShadow: '0 8px 18px -8px rgba(0,180,161,0.55)' }}>S</span>
              <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 19, letterSpacing: '-0.01em' }}>SaleDi</span>
            </div>
            <nav style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2, background: 'oklch(0.96 0.016 175)', border: '1px solid oklch(0.915 0.022 175)', borderRadius: 999, padding: 4 }}>
              <button type="button" onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: FONT, fontSize: 14, color: '#3C6367', padding: '8px 15px', borderRadius: 999, border: 0, background: 'transparent', cursor: 'pointer' }}>
                <LayoutDashboard style={{ width: 15, height: 15 }} /> Dashboard
              </button>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 500, color: '#fff', background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', padding: '8px 15px', borderRadius: 999 }}>
                <LayoutTemplate style={{ width: 15, height: 15 }} /> Sale Page Builder
              </span>
            </nav>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10 }} ref={menuRef}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#0E3A40', color: '#fff', fontSize: 12.5, fontWeight: 500, padding: '6px 13px', borderRadius: 999 }}>
                <Crown style={{ width: 13, height: 13, color: '#FF7A45' }} /> {planLabel(subscription?.plan)}
              </span>
              <button type="button" onClick={() => setMenuOpen((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: 8, border: 0, background: 'transparent', cursor: 'pointer', padding: 0 }}>
                <span style={{ width: 38, height: 38, borderRadius: 999, background: '#EAF1F1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <User style={{ width: 18, height: 18, color: '#3A93B9' }} />
                </span>
                <ChevronDown style={{ width: 15, height: 15, color: '#5B7D80' }} />
              </button>
              {menuOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, zIndex: 60, background: '#fff', border: '1px solid rgba(14,58,64,0.08)', borderRadius: 16, boxShadow: '0 26px 50px -28px rgba(14,58,64,0.4)', padding: 8, display: 'flex', flexDirection: 'column', minWidth: 200 }}>
                  <div onClick={() => { setMenuOpen(false); navigate('/dashboard'); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 11, fontSize: 13.5, color: INK, cursor: 'pointer' }}>
                    <LayoutDashboard style={{ width: 15, height: 15, color: '#00B4A1' }} /> แดชบอร์ด
                  </div>
                  <div onClick={() => { setMenuOpen(false); navigate('/salepage/settings'); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 11, fontSize: 13.5, color: INK, cursor: 'pointer' }}>
                    <Settings style={{ width: 15, height: 15, color: '#5B7D80' }} /> ตั้งค่า
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      {isPreviewMode && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 60, display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => setIsPreviewMode(false)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', color: INK, border: '1px solid rgba(14,58,64,0.1)', borderRadius: 999, padding: '10px 18px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', boxShadow: '0 16px 30px -18px rgba(14,58,64,0.4)' }}>
            <ArrowLeft style={{ width: 15, height: 15 }} /> กลับ
          </button>
          <button type="button" onClick={() => setIsPreviewMode(false)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', border: 0, borderRadius: 999, padding: '10px 18px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', boxShadow: '0 16px 30px -18px rgba(0,180,161,0.5)' }}>
            <Edit3 style={{ width: 15, height: 15 }} /> แก้ไข
          </button>
        </div>
      )}

      <main style={{ flex: 1, maxWidth: 1280, margin: '0 auto', width: '100%', padding: `clamp(18px,2.6vw,26px) clamp(16px,3vw,32px) clamp(48px,6vw,72px)`, display: 'flex', flexDirection: 'column', gap: 16 }}>

        {!isPreviewMode && cannotSave && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14, justifyContent: 'space-between', background: '#FFF1EC', border: '1px solid rgba(255,122,69,0.3)', borderRadius: 18, padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(255,122,69,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                <Clock style={{ width: 19, height: 19, color: '#B3552A' }} />
              </span>
              <div>
                <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 15, color: '#8A4A26' }}>แพ็คเกจหมดอายุแล้ว — ไม่สามารถแก้ไข Sale Page ได้</div>
                <div style={{ fontSize: 12.5, color: '#8A4A26' }}>กรุณาต่ออายุแพ็คเกจเพื่อแก้ไขและบันทึก Sale Page</div>
              </div>
            </div>
            <button type="button" onClick={() => navigate('/salepage/renew')} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', border: 0, borderRadius: 999, padding: '10px 18px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <CreditCard style={{ width: 15, height: 15 }} /> ต่ออายุเลย
            </button>
          </div>
        )}

        {!isPreviewMode && (
          <div style={{ background: '#0E3A40', borderRadius: 22, padding: 'clamp(16px,2vw,20px) clamp(18px,2.4vw,24px)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>ชื่อ Salepage</div>
              <input
                type="text"
                className="sp-toolbar-input"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (!salePageId && !slug) setSlug(generateSlug(e.target.value));
                }}
                placeholder="เช่น คอร์สเรียนภาษาอังกฤษ"
                style={{ width: '100%', maxWidth: 360, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, padding: '10px 14px', fontSize: 14.5, color: '#fff', fontFamily: FONT }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>สีพื้นหลัง</div>
              <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
                {recentColors.map((c) => (
                  <button
                    key={c}
                    type="button"
                    title={SWATCH_NAMES[c] || c}
                    onClick={() => pickBgColor(c)}
                    style={{
                      width: 26, height: 26, borderRadius: 999, cursor: 'pointer', padding: 0, background: c,
                      border: `2px solid ${backgroundColor === c ? '#00B4A1' : 'rgba(255,255,255,0.25)'}`,
                      boxShadow: backgroundColor === c
                        ? '0 0 0 3px rgba(0,180,161,0.25), inset 0 0 0 1px rgba(14,58,64,0.12)'
                        : 'inset 0 0 0 1px rgba(14,58,64,0.12)',
                    }}
                  />
                ))}
                <div style={{ position: 'relative', width: 34, height: 34 }} ref={customPickerRef}>
                  <button
                    type="button"
                    title="เลือกสีเอง"
                    onClick={() => setCustomPickerOpen((v) => !v)}
                    style={{
                      width: 34, height: 34, borderRadius: 999, padding: 0, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: `2px solid ${!recentColors.includes(backgroundColor) ? '#00B4A1' : 'rgba(255,255,255,0.25)'}`,
                      boxShadow: !recentColors.includes(backgroundColor) ? '0 0 0 3px rgba(0,180,161,0.25)' : '0 6px 14px -6px rgba(0,0,0,0.35)',
                      background: !recentColors.includes(backgroundColor) ? backgroundColor : 'linear-gradient(135deg, #00B4A1, #3A93B9)',
                    }}
                  >
                    <Palette style={{ width: 16, height: 16, color: '#fff', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }} />
                  </button>
                  {customPickerOpen && (
                    <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, zIndex: 90, background: '#fff', borderRadius: 18, padding: 14, boxShadow: '0 26px 50px -20px rgba(14,58,64,0.45)', width: 210, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <HexColorPicker
                        className="sp-color-picker"
                        color={/^#[0-9a-fA-F]{6}$/.test(backgroundColor) ? backgroundColor : '#ffffff'}
                        onChange={setBackgroundColor}
                        onChangeEnd={rememberRecentColor}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 30, height: 30, borderRadius: 9, background: backgroundColor, border: '1px solid rgba(14,58,64,0.12)', flex: '0 0 auto' }} />
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, border: '1px solid rgba(14,58,64,0.15)', borderRadius: 10, padding: '0 4px 0 10px' }}>
                          <span style={{ fontSize: 13, color: '#5B7D80' }}>#</span>
                          <HexColorInput className="sp-hex-input" color={backgroundColor} onChange={setBackgroundColor} onBlur={() => rememberRecentColor(backgroundColor)} prefixed={false} />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCustomPickerOpen(false)}
                        style={{ background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', border: 0, borderRadius: 10, padding: '9px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                      >
                        เสร็จสิ้น
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ position: 'relative' }} ref={themePickerRef}>
                <button type="button" title="เปลี่ยนสีปุ่มทั้งหน้า" onClick={() => setThemePickerOpen((v) => !v)} style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 11, cursor: 'pointer' }}>
                  <span style={{ width: 17, height: 17, borderRadius: 999, background: themeColor, border: '1.5px solid rgba(255,255,255,0.5)' }} />
                </button>
                {themePickerOpen && (
                  <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, zIndex: 95, background: '#fff', borderRadius: 18, padding: 14, boxShadow: '0 26px 50px -20px rgba(14,58,64,0.45)', width: 220, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: INK }}>เปลี่ยนสีปุ่มทุกบล็อกในหน้า</div>
                    <HexColorPicker
                      className="sp-color-picker"
                      color={/^#[0-9a-fA-F]{6}$/.test(themeColor) ? themeColor : '#00B4A1'}
                      onChange={setThemeColor}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 30, height: 30, borderRadius: 9, background: themeColor, border: '1px solid rgba(14,58,64,0.12)', flex: '0 0 auto' }} />
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, border: '1px solid rgba(14,58,64,0.15)', borderRadius: 10, padding: '0 4px 0 10px' }}>
                        <span style={{ fontSize: 13, color: '#5B7D80' }}>#</span>
                        <HexColorInput className="sp-hex-input" color={themeColor} onChange={setThemeColor} prefixed={false} />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => { applyButtonColorToAll(themeColor); setThemePickerOpen(false); }}
                      style={{ background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', border: 0, borderRadius: 10, padding: '9px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      ใช้กับทุกปุ่ม
                    </button>
                    <div style={{ fontSize: 11, color: '#5B7D80', lineHeight: 1.5 }}>ปุ่ม CTA, ฟอร์มติดต่อ, ชำระเงิน และแพ็คเก็จทุกอันจะเปลี่ยนเป็นสีนี้ทันที</div>
                  </div>
                )}
              </div>
              <button type="button" title="วิธีใช้งาน" onClick={() => navigate('/sale-page-help')} style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 11, cursor: 'pointer' }}>
                <HelpCircle style={{ width: 17, height: 17 }} />
              </button>
              <button type="button" title="ดูตัวอย่าง" onClick={() => setIsPreviewMode(true)} style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 11, cursor: 'pointer' }}>
                <Eye style={{ width: 17, height: 17 }} />
              </button>
              <span style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.15)', margin: '0 2px' }} />
              <button type="button" onClick={openAiModal} style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'linear-gradient(120deg, #FF7A45, #FFB84D)', color: '#fff', border: 0, borderRadius: 11, padding: '10px 15px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', boxShadow: '0 14px 26px -14px rgba(255,122,69,0.6)' }}>
                <Wand2 style={{ width: 15, height: 15 }} /> สร้างด้วย AI {!isVip && <Crown style={{ width: 13, height: 13 }} />}
              </button>
              <button type="button" onClick={onSaveClick} disabled={isSaving || cannotSave} style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', border: 0, borderRadius: 11, padding: '10px 17px', fontSize: 13.5, fontWeight: 600, cursor: isSaving || cannotSave ? 'default' : 'pointer', boxShadow: '0 14px 26px -14px rgba(0,180,161,0.6)', opacity: isSaving || cannotSave ? 0.6 : 1 }}>
                <Save style={{ width: 15, height: 15 }} /> {isSaving ? 'กำลังบันทึก...' : 'บันทึก'}
              </button>
            </div>
          </div>
        )}

        <div style={{ background: isPreviewMode ? 'transparent' : '#fff', borderRadius: isPreviewMode ? 0 : 26, padding: isPreviewMode ? 0 : 2, border: isPreviewMode ? 'none' : '1px solid rgba(14,58,64,0.06)', overflow: 'hidden' }}>
          <div style={{ background: backgroundColor, borderRadius: isPreviewMode ? 0 : 24, minHeight: blocks.length === 0 ? 'clamp(360px,48vw,460px)' : undefined, display: blocks.length === 0 ? 'flex' : 'block', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, padding: blocks.length === 0 ? 'clamp(32px,5vw,56px) 20px' : (isPreviewMode ? '0' : 'clamp(16px,2.4vw,24px)'), textAlign: blocks.length === 0 ? 'center' : 'left' }}>

            {blocks.length === 0 && !isPreviewMode && (
              <>
                <span style={{ width: 64, height: 64, borderRadius: 999, background: 'linear-gradient(135deg, #00B4A1, #3A93B9)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 20px 40px -16px rgba(0,180,161,0.5)' }}>
                  <Sparkles style={{ width: 28, height: 28, color: '#fff' }} />
                </span>
                <div>
                  <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 'clamp(19px,2.4vw,23px)' }}>เริ่มสร้างหน้าขายของคุณ</div>
                  <div style={{ fontSize: 13.5, color: '#5B7D80', marginTop: 4 }}>เลือกบล็อกที่ต้องการด้านล่าง แล้วกดเพิ่มได้เลย ไม่ต้องเขียนโค้ด</div>
                </div>
                <button type="button" onClick={() => setPickerOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', border: 0, borderRadius: 999, padding: '13px 22px', fontSize: 14.5, fontWeight: 600, cursor: 'pointer', boxShadow: '0 16px 30px -16px rgba(0,180,161,0.55)' }}>
                  <Plus style={{ width: 17, height: 17 }} /> เพิ่มบล็อก
                </button>
              </>
            )}

            {blocks.length === 0 && isPreviewMode && (
              <div style={{ padding: '80px 20px', textAlign: 'center', color: 'rgba(14,58,64,0.4)', fontSize: 14 }}>ยังไม่มีเนื้อหา</div>
            )}

            {blocks.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: isPreviewMode ? 0 : 12 }}>
                {blocks.map((block, index) => (
                  <div key={block.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    {!isPreviewMode && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 2 }}>
                        <button type="button" onClick={() => moveBlock(block.id, 'up')} disabled={index === 0} style={ctrlBtn('rgba(58,147,185,0.12)', '#276E8A', index === 0)}>
                          <ChevronUp style={{ width: 14, height: 14 }} />
                        </button>
                        <button type="button" onClick={() => moveBlock(block.id, 'down')} disabled={index === blocks.length - 1} style={ctrlBtn('rgba(58,147,185,0.12)', '#276E8A', index === blocks.length - 1)}>
                          <ChevronDown style={{ width: 14, height: 14 }} />
                        </button>
                        <button type="button" onClick={() => deleteBlock(block.id)} style={ctrlBtn('rgba(255,90,90,0.12)', '#C0392B')}>
                          <Trash2 style={{ width: 14, height: 14 }} />
                        </button>
                      </div>
                    )}
                    <div style={!isPreviewMode ? { flex: 1, background: '#fff', borderRadius: 16, border: '1px solid rgba(14,58,64,0.08)', boxShadow: '0 16px 32px -28px rgba(14,58,64,0.35)', padding: 16 } : { flex: 1 }}>
                      {renderBlock(block)}
                    </div>
                  </div>
                ))}

                {!isPreviewMode && (
                  <button type="button" onClick={() => setPickerOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px auto 0', background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', border: 0, borderRadius: 999, padding: '12px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 14px 26px -14px rgba(0,180,161,0.5)' }}>
                    <Plus style={{ width: 16, height: 16 }} /> เพิ่มบล็อก
                  </button>
                )}
              </div>
            )}

            {!isPreviewMode && pickerOpen && PickerModal}
          </div>
        </div>

        {!isPreviewMode && (
          <div style={{ background: '#fff', borderRadius: 20, padding: 'clamp(14px,1.8vw,18px) clamp(18px,2.2vw,22px)', border: '1px solid rgba(14,58,64,0.06)', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {!contactEditing ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14 }}>
                <span style={{ width: 36, height: 36, borderRadius: 11, background: 'rgba(91,111,203,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                  <PhoneCall style={{ width: 17, height: 17, color: '#4A57A8' }} />
                </span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 14 }}>ช่องทางติดต่อลูกค้า</div>
                  <div style={{ fontSize: 12.5, color: '#5B7D80' }}>
                    {hasAnyLink
                      ? [socialLinks.facebook && 'Facebook', socialLinks.instagram && 'Instagram', socialLinks.line && 'LINE', socialLinks.phone && socialLinks.phone].filter(Boolean).join(' · ')
                      : 'ยังไม่ได้เพิ่มช่องทางติดต่อ เช่น LINE, เบอร์โทร, Facebook'}
                  </div>
                </div>
                <button type="button" onClick={openContactEdit} style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#F3F8F8', color: INK, border: '1px solid rgba(14,58,64,0.1)', borderRadius: 11, padding: '9px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                  <Pencil style={{ width: 14, height: 14 }} /> แก้ไข
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 14 }}>ช่องทางติดต่อลูกค้า</div>
                <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Facebook style={{ width: 17, height: 17, color: '#276E8A', flex: '0 0 auto' }} />
                    <div style={{ flex: 1, display: 'flex', alignItems: 'stretch', border: '1px solid rgba(14,58,64,0.12)', borderRadius: 10, overflow: 'hidden', minWidth: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 10px', background: '#F3F8F8', color: '#5B7D80', fontSize: 12.5, whiteSpace: 'nowrap', flex: '0 0 auto' }}>facebook.com/</span>
                      <input type="text" placeholder="username" value={tempSocialLinks.facebook || ''} onChange={(e) => setTempSocialLinks({ ...tempSocialLinks, facebook: e.target.value })} style={{ flex: 1, minWidth: 0, padding: '9px 10px', border: 0, fontSize: 13, fontFamily: FONT }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Instagram style={{ width: 17, height: 17, color: '#B3552A', flex: '0 0 auto' }} />
                    <div style={{ flex: 1, display: 'flex', alignItems: 'stretch', border: '1px solid rgba(14,58,64,0.12)', borderRadius: 10, overflow: 'hidden', minWidth: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 10px', background: '#F3F8F8', color: '#5B7D80', fontSize: 12.5, whiteSpace: 'nowrap', flex: '0 0 auto' }}>instagram.com/</span>
                      <input type="text" placeholder="username" value={tempSocialLinks.instagram || ''} onChange={(e) => setTempSocialLinks({ ...tempSocialLinks, instagram: e.target.value })} style={{ flex: 1, minWidth: 0, padding: '9px 10px', border: 0, fontSize: 13, fontFamily: FONT }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <MessageCircle style={{ width: 17, height: 17, color: '#0E7C86', flex: '0 0 auto' }} />
                    <div style={{ flex: 1, display: 'flex', alignItems: 'stretch', border: '1px solid rgba(14,58,64,0.12)', borderRadius: 10, overflow: 'hidden', minWidth: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 10px', background: '#F3F8F8', color: '#5B7D80', fontSize: 12.5, whiteSpace: 'nowrap', flex: '0 0 auto' }}>line.me/ti/p/@</span>
                      <input type="text" placeholder="lineid" value={tempSocialLinks.line || ''} onChange={(e) => setTempSocialLinks({ ...tempSocialLinks, line: e.target.value })} style={{ flex: 1, minWidth: 0, padding: '9px 10px', border: 0, fontSize: 13, fontFamily: FONT }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <PhoneCall style={{ width: 17, height: 17, color: '#5B7D80', flex: '0 0 auto' }} />
                    <input type="tel" placeholder="0812345678" value={tempSocialLinks.phone || ''} onChange={(e) => setTempSocialLinks({ ...tempSocialLinks, phone: e.target.value })} style={{ flex: 1, padding: '9px 12px', border: '1px solid rgba(14,58,64,0.12)', borderRadius: 10, fontSize: 13, fontFamily: FONT }} />
                  </div>
                </div>
                <div style={{ fontSize: 11.5, color: '#8AA6A8' }}>
                  ใส่แค่ชื่อผู้ใช้ เช่น <b style={{ color: '#5B7D80' }}>viitamint.thanat</b> ระบบจะเปิดลิงก์โปรไฟล์ให้อัตโนมัติ (วางลิงก์เต็มก็ยังใช้ได้)
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setContactEditing(false)} style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#F3F8F8', color: INK, border: '1px solid rgba(14,58,64,0.1)', borderRadius: 11, padding: '9px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                    <X style={{ width: 14, height: 14 }} /> ยกเลิก
                  </button>
                  <button type="button" onClick={saveContact} style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', border: 0, borderRadius: 11, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    <Save style={{ width: 14, height: 14 }} /> บันทึก
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </main>

      {showUrlModal && generatedUrl && (
        <div onClick={() => setShowUrlModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(14,58,64,0.4)', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 24, padding: 28, width: '100%', maxWidth: 440, boxShadow: '0 40px 80px -30px rgba(14,58,64,0.5)', position: 'relative' }}>
            <button type="button" onClick={() => setShowUrlModal(false)} style={{ position: 'absolute', top: 16, right: 16, background: 'transparent', border: 0, cursor: 'pointer', color: '#5B7D80' }}>
              <X style={{ width: 20, height: 20 }} />
            </button>
            <div style={{ textAlign: 'center' }}>
              <span style={{ width: 60, height: 60, borderRadius: 999, background: 'linear-gradient(135deg, #00B4A1, #3A93B9)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Save style={{ width: 26, height: 26, color: '#fff' }} />
              </span>
              <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 20, marginBottom: 4 }}>บันทึกสำเร็จ!</div>
              <div style={{ fontSize: 13.5, color: '#5B7D80', marginBottom: 20 }}>Sale Page ของคุณพร้อมใช้งานแล้ว</div>
              <div style={{ background: '#F3F8F8', borderRadius: 14, padding: 14, marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: '#5B7D80', marginBottom: 4 }}>URL ของคุณ:</div>
                <a href={`https://${generatedUrl}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3A93B9', fontWeight: 600, fontSize: 13.5, wordBreak: 'break-all' }}>{generatedUrl}</a>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => { navigator.clipboard.writeText(`https://${generatedUrl}`); alert('คัดลอก URL แล้ว!'); }} style={{ flex: 1, padding: '10px 0', background: '#F3F8F8', color: INK, border: '1px solid rgba(14,58,64,0.1)', borderRadius: 12, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>คัดลอก URL</button>
                <button type="button" onClick={() => { setShowUrlModal(false); navigate('/dashboard'); }} style={{ flex: 1, padding: '10px 0', background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', border: 0, borderRadius: 12, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>ไปที่ Dashboard</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {aiModalOpen && (
        <div onClick={() => !aiGenerating && setAiModalOpen(false)} style={overlayStyle}>
          <div onClick={(e) => e.stopPropagation()} style={{ ...sheetStyle, padding: 0, overflow: 'hidden' }}>
            {/* Hero header */}
            <div style={{ position: 'relative', background: 'linear-gradient(135deg, #0E3A40 0%, #14545C 55%, #1A6A64 100%)', padding: narrow ? '18px 20px 20px' : '22px 26px 24px', color: '#fff', flex: '0 0 auto' }}>
              <div style={{ position: 'absolute', top: -50, right: -40, width: 180, height: 180, borderRadius: 999, background: 'radial-gradient(circle, rgba(255,140,70,0.35), transparent 70%)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', bottom: -60, left: -30, width: 160, height: 160, borderRadius: 999, background: 'radial-gradient(circle, rgba(0,180,161,0.3), transparent 70%)', pointerEvents: 'none' }} />
              {narrow && <div style={{ width: 36, height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.25)', margin: '0 auto 14px' }} />}
              <button type="button" onClick={() => !aiGenerating && setAiModalOpen(false)} style={{ position: 'absolute', top: 14, right: 14, width: 30, height: 30, borderRadius: 999, border: 0, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2 }}>
                <X style={{ width: 15, height: 15, color: 'rgba(255,255,255,0.85)' }} />
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative' }}>
                <span style={{ width: 48, height: 48, borderRadius: 15, background: 'linear-gradient(135deg, #FF7A45, #FFB84D)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 26px -10px rgba(255,122,69,0.7)', flex: '0 0 auto' }}>
                  <Wand2 style={{ width: 23, height: 23, color: '#fff' }} />
                </span>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 'clamp(16px,2.2vw,19px)' }}>สร้าง Sale Page ด้วย AI</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'linear-gradient(120deg, #F5C24B, #E8A020)', color: '#5A3A00', fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: '3px 9px' }}>
                      <Crown style={{ width: 11, height: 11 }} /> PREMIUM VIP
                    </span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>บอกแค่ว่าขายอะไร AI จัดหน้าขายครบ 8 ส่วนให้ในไม่กี่วินาที</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', position: 'relative' }}>
                {['โครงสร้างมาตรฐาน 8 ส่วน', 'เขียนคำขายให้อัตโนมัติ', 'จัดวางรูปของคุณให้เอง'].map((t) => (
                  <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 999, padding: '5px 11px', fontSize: 11.5, color: 'rgba(255,255,255,0.9)' }}>
                    <Sparkles style={{ width: 11, height: 11, color: '#FFB84D' }} /> {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16, padding: narrow ? '18px 20px 20px' : '20px 26px 24px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
                  <span style={{ width: 21, height: 21, borderRadius: 999, background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', fontSize: 11.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>1</span>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: INK }}>อธิบายสินค้า/บริการของคุณ</span>
                </div>
                <textarea
                  value={aiDescription}
                  onChange={(e) => setAiDescription(e.target.value)}
                  disabled={aiGenerating}
                  placeholder={'ยิ่งบอกละเอียด ยิ่งได้หน้าที่ตรงใจ เช่น\n• ขายอะไร จุดเด่นคืออะไร\n• ราคา / โปรโมชั่น (ถ้ามี)\n• กลุ่มลูกค้าเป้าหมาย\n• ช่องทางติดต่อ เช่น LINE'}
                  rows={5}
                  style={{ width: '100%', padding: '12px 14px', border: '1.5px solid rgba(14,58,64,0.12)', borderRadius: 14, fontSize: 13.5, fontFamily: FONT, resize: 'vertical', lineHeight: 1.6, background: aiGenerating ? '#F7FAFA' : '#fff' }}
                />
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
                  <span style={{ width: 21, height: 21, borderRadius: 999, background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', fontSize: 11.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>2</span>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: INK }}>ใส่รูปสินค้า/ผลงาน</span>
                  <span style={{ fontSize: 11.5, color: '#8AA6A8' }}>(ไม่เกิน 4 รูป · รูปแรกเป็นภาพปก)</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {aiImages.map((f, i) => (
                    <div key={i} style={{ position: 'relative', width: 76, height: 76, borderRadius: 14, overflow: 'hidden', border: i === 0 ? '2px solid #FF7A45' : '1px solid rgba(14,58,64,0.12)', boxShadow: '0 8px 18px -10px rgba(14,58,64,0.3)' }}>
                      <img src={URL.createObjectURL(f)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      {i === 0 && (
                        <span style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(120deg, #FF7A45, #FFB84D)', color: '#fff', fontSize: 9.5, fontWeight: 700, textAlign: 'center', padding: '2px 0' }}>ภาพปก</span>
                      )}
                      {!aiGenerating && (
                        <button type="button" onClick={() => removeAiImage(i)} style={{ position: 'absolute', top: 3, right: 3, width: 19, height: 19, borderRadius: 999, background: 'rgba(14,58,64,0.75)', color: '#fff', border: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                          <X style={{ width: 11, height: 11 }} />
                        </button>
                      )}
                    </div>
                  ))}
                  {aiImages.length < 4 && !aiGenerating && (
                    <label style={{ width: 76, height: 76, borderRadius: 14, border: '1.5px dashed rgba(0,180,161,0.45)', background: 'rgba(0,180,161,0.05)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, cursor: 'pointer', color: '#00A392' }}>
                      <ImagePlus style={{ width: 20, height: 20 }} />
                      <span style={{ fontSize: 10, fontWeight: 600 }}>เพิ่มรูป</span>
                      <input type="file" accept="image/*" multiple hidden onChange={(e) => addAiImages(e.target.files)} />
                    </label>
                  )}
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
                  <span style={{ width: 21, height: 21, borderRadius: 999, background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', fontSize: 11.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>3</span>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: INK }}>โทนสีแบรนด์</span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setAiUseCustomColors(false)}
                    disabled={aiGenerating}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 999, padding: '8px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: !aiUseCustomColors ? '1.5px solid #00B4A1' : '1.5px solid rgba(14,58,64,0.15)', background: !aiUseCustomColors ? 'rgba(0,180,161,0.08)' : '#fff', color: !aiUseCustomColors ? '#00806F' : '#5B7D80', fontFamily: FONT }}
                  >
                    <Sparkles style={{ width: 13, height: 13 }} /> ให้ AI เลือกสีให้
                  </button>
                  <button
                    type="button"
                    onClick={() => setAiUseCustomColors(true)}
                    disabled={aiGenerating}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 999, padding: '8px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', border: aiUseCustomColors ? '1.5px solid #00B4A1' : '1.5px solid rgba(14,58,64,0.15)', background: aiUseCustomColors ? 'rgba(0,180,161,0.08)' : '#fff', color: aiUseCustomColors ? '#00806F' : '#5B7D80', fontFamily: FONT }}
                  >
                    <Palette style={{ width: 13, height: 13 }} /> เลือกสีเอง
                  </button>
                  {aiUseCustomColors && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <ColorSwatchPicker compact color={aiBrandPrimary} onChange={setAiBrandPrimary} />
                        <span style={{ fontSize: 12.5, color: '#5B7D80', fontWeight: 600 }}>สีหลัก</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <ColorSwatchPicker compact color={aiBrandSecondary} fallback="#FF7A45" onChange={setAiBrandSecondary} />
                        <span style={{ fontSize: 12.5, color: '#5B7D80', fontWeight: 600 }}>สีรอง</span>
                      </div>
                    </div>
                  )}
                </div>
                {aiUseCustomColors && (
                  <div style={{ fontSize: 11.5, color: '#8AA6A8', marginTop: 6 }}>สีหลักใช้กับพาดหัว หัวข้อ ปุ่ม และไอคอนทั้งหน้า · สีรองใช้เน้นคำสำคัญ/ข้อเสนอพิเศษ</div>
                )}
              </div>

              {aiError && (
                <div style={{ fontSize: 12.5, color: '#C0392B', background: 'rgba(255,90,90,0.08)', border: '1px solid rgba(255,90,90,0.2)', borderRadius: 12, padding: '11px 14px' }}>{aiError}</div>
              )}

              {aiGenerating ? (
                <div style={{ background: 'linear-gradient(135deg, rgba(255,122,69,0.07), rgba(255,184,77,0.1))', border: '1px solid rgba(255,122,69,0.25)', borderRadius: 16, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <span style={{ width: 40, height: 40, borderRadius: 999, background: 'linear-gradient(135deg, #FF7A45, #FFB84D)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                    <Loader2 style={{ width: 20, height: 20, color: '#fff' }} className="animate-spin" />
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#8A4A26' }}>{AI_STAGES[aiStageIdx]}</div>
                    <div style={{ fontSize: 11.5, color: '#B3763F', marginTop: 2 }}>ใช้เวลาประมาณ 15-30 วินาที อย่าเพิ่งปิดหน้านี้</div>
                    <div style={{ height: 5, borderRadius: 999, background: 'rgba(255,122,69,0.15)', marginTop: 8, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${((aiStageIdx + 1) / AI_STAGES.length) * 90}%`, borderRadius: 999, background: 'linear-gradient(90deg, #FF7A45, #FFB84D)', transition: 'width 0.8s ease' }} />
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={generateWithAi}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, background: 'linear-gradient(120deg, #FF7A45, #FFB84D)', color: '#fff', border: 0, borderRadius: 999, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: '0 18px 34px -14px rgba(255,122,69,0.6)' }}
                >
                  <Sparkles style={{ width: 17, height: 17 }} /> สร้าง Sale Page เลย
                </button>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11.5, color: '#8AA6A8' }}>
                <Wand2 style={{ width: 12, height: 12 }} /> ใช้ได้ 10 ครั้ง/เดือน · สร้างเสร็จแล้วแก้ไขข้อความ รูป และสีต่อได้ทุกจุด
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalePage;
